export const EMPTY_DOCTOR_FORM = {
	name: "",
	email: "",
	phoneNumber: "",
	phoneCode: "+91",
	registrationNumber: "",
	qualification: "",
	speciality: "",
	clinicName: "",
	clinicAddress: "",
	note: "",
};

export const formatPhoneCode = (code) => {
	if (!code) return "+91";
	const trimmed = String(code).trim();
	return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
};

export const normalizeDoctorName = (name = "") =>
	String(name).replace(/\s+/g, " ").trim().toLowerCase();

export const isPlaceholderDoctorName = (name = "") => {
	const normalized = normalizeDoctorName(name);
	return !normalized || normalized === "n/a" || normalized === "na" || normalized === "none";
};

export const sanitizeDoctorName = (name = "") => {
	const trimmed = String(name || "").trim();
	return isPlaceholderDoctorName(trimmed) ? "" : trimmed;
};

export const normalizePhoneDigits = (value = "") => String(value).replace(/\D/g, "");

export const hasDoctorFormInput = (form = {}) =>
	Object.entries(form).some(([key, value]) => {
		if (key === "phoneCode") return false;
		if (key === "name" && isPlaceholderDoctorName(value)) return false;
		return String(value || "").trim() !== "";
	});

export const mapPrescriptionDoctorToForm = (doctor = {}) => {
	const specialities = doctor.specialities || doctor.specialties;
	const speciality = Array.isArray(specialities)
		? specialities.filter(Boolean).join(", ")
		: doctor.speciality || doctor.specialty || "";

	return {
		name: sanitizeDoctorName(doctor.name),
		email: doctor.email || "",
		phoneNumber: String(doctor.phoneNumber || doctor.mobile || "").trim(),
		phoneCode: formatPhoneCode(doctor.phoneCode || doctor.phone_code),
		registrationNumber: doctor.registrationNumber || doctor.registration_number || "",
		qualification: doctor.qualification || "",
		speciality,
		clinicName: doctor.clinicName || doctor.clinic_name || "",
		clinicAddress: doctor.clinicAddress || doctor.clinic_address || "",
		note: doctor.note || "",
	};
};

export const mapMasterDoctorToForm = (doctor = {}) => mapPrescriptionDoctorToForm(doctor);

export const buildDoctorApiPayload = (doctorForm = {}) => ({
	name: doctorForm.name?.trim() || "",
	email: doctorForm.email?.trim() || "",
	phoneNumber: doctorForm.phoneNumber?.trim() || "",
	phoneCode: doctorForm.phoneCode || "",
	registrationNumber: doctorForm.registrationNumber?.trim() || "",
	qualification: doctorForm.qualification?.trim() || "",
	speciality: doctorForm.speciality?.trim() || "",
	clinicName: doctorForm.clinicName?.trim() || "",
	clinicAddress: doctorForm.clinicAddress?.trim() || "",
	note: doctorForm.note?.trim() || "",
});

export const buildDoctorSelectOption = (doctor = {}) => {
	const parts = [doctor.name];
	if (doctor.phoneNumber) parts.push(doctor.phoneNumber);
	if (doctor.email) parts.push(doctor.email);

	return {
		value: doctor._id,
		label: parts.filter(Boolean).join(" • "),
		data: doctor,
	};
};

export const extractCreatedDoctor = (response) =>
	response?.data?.data?.doctor ||
	response?.data?.data ||
	response?.data?.doctor ||
	{};

export const extractCreatedDoctorId = (response) => {
	const doctor = extractCreatedDoctor(response);
	return doctor?._id || response?.data?.data?.doctorId || null;
};

export const validateDoctorFormForCreate = (doctorForm = {}, t = (value) => value) => {
	const errors = {};
	if (!doctorForm.name?.trim()) {
		errors.doctorName = t("Doctor name is required");
	}
	if (
		doctorForm.email?.trim() &&
		!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(doctorForm.email.trim())
	) {
		errors.doctorEmail = t("Enter a valid email address");
	}
	return errors;
};

/**
 * Pick the best doctor from master search results for a prescription / form source.
 */
export const pickBestDoctorMatch = (doctors = [], source = {}) => {
	if (!Array.isArray(doctors) || doctors.length === 0) return null;

	const sourceName = normalizeDoctorName(source.name);
	const sourceEmail = String(source.email || "").trim().toLowerCase();
	const sourcePhone = normalizePhoneDigits(source.phoneNumber || source.mobile);

	if (sourceEmail) {
		const byEmail = doctors.find(
			(doctor) => String(doctor.email || "").trim().toLowerCase() === sourceEmail
		);
		if (byEmail) return byEmail;
	}

	if (sourcePhone) {
		const byPhone = doctors.find(
			(doctor) => normalizePhoneDigits(doctor.phoneNumber) === sourcePhone
		);
		if (byPhone) return byPhone;
	}

	if (sourceName) {
		const byName = doctors.find(
			(doctor) => normalizeDoctorName(doctor.name) === sourceName
		);
		if (byName) return byName;
	}

	return doctors.length === 1 ? doctors[0] : null;
};

export const buildOptionalSaleMeta = ({
	doctorId,
	doctorForm,
	prescriptionFile,
	prescriptionDate,
	includePrescriptionDate = false,
}) => {
	const meta = {};
	if (doctorId) {
		meta.doctorId = doctorId;
	}
	const note = doctorForm?.note?.trim();
	if (note) {
		meta.note = note;
	}
	if (prescriptionDate?.trim() && (prescriptionFile || includePrescriptionDate)) {
		meta.prescriptionDate = prescriptionDate.trim();
	}
	return meta;
};

const formatDoctorPhone = (doctorForm = {}) => {
	const phoneNumber = String(doctorForm.phoneNumber || "").trim();
	if (!phoneNumber) return "";

	const dialCode = String(doctorForm.phoneCode || "")
		.trim()
		.replace(/\D/g, "");
	if (!dialCode) return phoneNumber;

	return `+${dialCode} ${phoneNumber}`;
};

/**
 * Build prescribed doctor block for invoice display.
 * Requires doctor name; includes first available contact (phone, email, clinic).
 */
export const buildInvoiceDoctorInfo = (doctorForm = {}) => {
	const name = doctorForm?.name?.trim();
	if (!name) return null;

	const phone = formatDoctorPhone(doctorForm);
	const email = doctorForm?.email?.trim();
	const clinicName = doctorForm?.clinicName?.trim();

	if (phone) {
		return { name, contact: phone, contactLabel: "phone" };
	}
	if (email) {
		return { name, contact: email, contactLabel: "email" };
	}
	if (clinicName) {
		return { name, contact: clinicName, contactLabel: "clinic" };
	}

	return { name, contact: null, contactLabel: null };
};
